import { SelectOption } from '@focaldata/cin-ui-components'
import { useMemo } from 'react'
import { isMultilangPocEnabled } from '../../../constants/featureFlags'
import {
  useAddFdChatSpecification,
  useGetFdChatSpecification
} from '../../../containers/Page/FdChat/FdChat.hooks'
import {
  AdHocTestingSpecification,
  UpdateFieldworkV2Input
} from '../../../data/gql-gen/questionnaire/graphql'
import { draftAudienceRefetchQuery } from '../../../data/gql/questionnaire/queries/audiences'
import { useUpdateFieldworkV2 } from '../../../hooks/audience/createOrUpdateFieldworkV2'
import { useAudienceCountries } from '../../../hooks/audience/useAudienceCountries'
import { useGetPresetAudiences } from '../../../hooks/audience/usePresetAudience'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../hooks/useIsAudienceReadOnly'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import {
  dedupeCountriesToCountryOptions,
  reorderCountriesDropDown
} from '../../../utils/HelperFunctions'
import {
  PanelSupplier,
  convertAudienceType
} from '../../../utils/audienceHelper'
import { useProjectType } from '../../Project/Project.hooks'
import { defaultAllocationsForCountry } from '../LanguageAllocations/LanguageAllocations.utils'
import SelectLoading from '../SelectLoading'
import CountryPickerControl from './CountryPicker.control'
import { prolificCountries } from './ProlificAudience'

const CountryPicker = ({
  setShowErrorPage
}: {
  setShowErrorPage?: (show: true) => void
}) => {
  const { isReadOnly } = useIsAudienceReadOnly()
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { isFdChat } = useProjectType()
  const { addFdChatSpecification } = useAddFdChatSpecification()
  const { fdChatSpecification } = useGetFdChatSpecification()

  const { fieldwork, loading: getSupplierFieldworkLoading } = useFieldwork()

  const panelSupplierCode = fieldwork?.audience[0]?.__typename
    ? convertAudienceType(fieldwork.audience[0].__typename)
    : PanelSupplier.PURESPECTRUM

  const {
    data: dataCountries,
    loading: getCountriesLoading,
    error: getCountriesError
  } = useAudienceCountries(panelSupplierCode)

  const availableCountries = useMemo(
    () =>
      fieldwork?.audience[0]?.__typename === 'ProlificAudience'
        ? prolificCountries
        : dataCountries ?? [],
    [fieldwork?.audience, dataCountries]
  )

  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const { data: presetAudiences } = useGetPresetAudiences('GB', 'en')

  const handleChangeCountry = async (optionValue: string): Promise<void> => {
    // Option value shape depends on the flag:
    //   - Flag ON  → de-duplicated dropdown, value is just the countryCode
    //   - Flag OFF → legacy "CC-lang" format from reorderCountriesDropDown
    // For the ON path we pick whichever language record GET_COUNTRIES_V2
    // returns first for that country as the default — the user can
    // change / extend it via the LanguageAllocations editor afterwards.
    const newLocale = isMultilangPocEnabled()
      ? availableCountries.find(
          ({ countryCode }) => countryCode === optionValue
        )?.locale
      : (() => {
          const [cc, lang] = optionValue.split('-')
          return availableCountries.find(
            ({ countryCode, locale }) =>
              countryCode === cc && locale.language === lang
          )?.locale
        })()

    if (newLocale) {
      const audience = fieldwork?.audience[0]
      const panelSupplierCode = convertAudienceType(audience?.__typename)

      // When the multi-language flow is on for PureSpectrum, seed every
      // available language for the new country: the curated split from
      // getCountriesV2 (dominant language first) when present, otherwise an
      // even split (a single-language country lands at one language / 100%).
      // This:
      //   1. surfaces the country's languages in the LanguageAllocations
      //      editor as soon as a country is picked — the researcher can
      //      remove languages down to one
      //   2. wipes any allocations that were valid for the previous country
      //      but aren't for the new one
      // Percentages (not absolute samples) split the survey-level
      // samplesNeeded total, which stays the single source of truth. Gated by
      // isMultilangPocEnabled() (the POC kill-switch for the whole
      // audience-page multi-language flow) AND the PureSpectrum supplier (the
      // only one with multi-language fulfillment support, matching
      // <LanguageAllocationsContainer />'s render gating in Audience.tsx).
      const seedAllocations =
        isMultilangPocEnabled() &&
        panelSupplierCode === PanelSupplier.PURESPECTRUM
      const allocations = seedAllocations
        ? defaultAllocationsForCountry(availableCountries, newLocale.country)
        : []

      const updateFieldworkInput: UpdateFieldworkV2Input = {
        surveyId,
        panelSupplierCode: panelSupplierCode,
        countryCode: newLocale.country,
        // Primary locale language = the dominant allocation when seeding,
        // otherwise the picked locale's language.
        countryLanguage:
          allocations.length > 0 ? allocations[0].language : newLocale.language
      }

      if (seedAllocations) {
        // languageAllocations on UpdateFieldworkV2Input is a tri-state
        // optional field ({ value: [...] }) — wrap accordingly.
        updateFieldworkInput.languageAllocations = { value: allocations }
      }

      if (
        isFdChat &&
        newLocale.country === 'GB' &&
        newLocale.language === 'en'
      ) {
        const preset = presetAudiences?.find(
          (presetAudience) => presetAudience.name === 'Custom'
        )

        if (preset?.presetAudienceId) {
          updateFieldworkInput.presetAudienceId = preset.presetAudienceId
        }
      }

      await updateFieldworkV2({
        variables: {
          input: updateFieldworkInput
        },
        refetchQueries: [draftAudienceRefetchQuery(projectId, surveyId, waveId)]
      })

      if (isFdChat && fdChatSpecification) {
        const currentFdChatSpecification =
          fdChatSpecification as AdHocTestingSpecification

        addFdChatSpecification({
          variables: {
            input: {
              fdChatSpecification: {
                value: currentFdChatSpecification
              },
              projectId,
              surveyId,
              waveId
            }
          }
        })
      }
    }
  }

  // Flag-gated country options:
  //   - Flag ON  → one row per country (de-duplicated). Languages live in
  //                the LanguageAllocations editor so the country dropdown
  //                doesn't need to expose them.
  //   - Flag OFF → legacy "Canada - French" / "Canada - English" layout
  //                via the shared `reorderCountriesDropDown` helper (also
  //                used by CreateProject / AddMarketsDialog).
  const countries: SelectOption[] = useMemo(
    () =>
      isMultilangPocEnabled()
        ? dedupeCountriesToCountryOptions(availableCountries)
        : reorderCountriesDropDown(availableCountries),
    [availableCountries]
  )

  const { country, language } = fieldwork?.locale || {
    country: 'GB',
    language: 'en'
  }
  // Match the option-value shape produced above so the Select can resolve
  // the current selection.
  const selectedCountryCode: string = isMultilangPocEnabled()
    ? country
    : `${country}-${language}`

  if (getCountriesLoading || getSupplierFieldworkLoading) {
    return <SelectLoading label="Country" />
  }

  if (getCountriesError) {
    setShowErrorPage?.(true)
  }

  return (
    <CountryPickerControl
      defaultCountryCode={selectedCountryCode}
      countries={countries}
      onChangeSelectCountry={
        isReadOnly
          ? undefined
          : async (event) => {
              await handleChangeCountry(event.target.value)
            }
      }
    />
  )
}

export default CountryPicker
